<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import bannerArrowUrl from './assets/banner-arrow.webp'
import bannerCameraUrl from './assets/banner-camera.webp'
import bannerClothUrl from './assets/banner-cloth.webp'
import bannerImageUrl from './assets/banner-image.webp'
import bannerLikeUrl from './assets/banner-like.webp'
import bannerPlusUrl from './assets/banner-plus.webp'
import bannerQuestionUrl from './assets/banner-question.webp'
import bannerSearchUrl from './assets/banner-search.webp'
import { FAIR_HOME_BANNER_BG_URL } from './fair-home-lcp-assets'

interface FairHomeHeroBannerHowToProps {
  /** `sheet` = mobile drawer <768 (Figma 1:12418) */
  variant?: 'desktop' | 'sheet'
}

const props = withDefaults(defineProps<FairHomeHeroBannerHowToProps>(), {
  variant: 'desktop',
})

const { t } = useI18n()

const rootClass = computed(() => ({
  'fair-home-hero-banner-slide': true,
  'fair-home-hero-banner-slide--how-to': true,
  'fair-home-hero-banner-slide--sheet': props.variant === 'sheet',
}))
</script>

<template>
  <div
    :class="rootClass"
    :data-figma="variant === 'sheet' ? '1:12418' : '1:8863'"
  >
    <div
      class="fair-home-hero-banner-slide__media fair-home-hero-banner-slide__media--back"
      aria-hidden="true"
    >
      <img
        class="fair-home-hero-banner-slide__bg"
        :src="FAIR_HOME_BANNER_BG_URL"
        alt=""
        fetchpriority="high"
        decoding="async"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--plus"
        :src="bannerPlusUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--camera"
        :src="bannerCameraUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--arrow"
        :src="bannerArrowUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--image"
        :src="bannerImageUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--like"
        :src="bannerLikeUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--cloth"
        :src="bannerClothUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
    </div>

    <p class="fair-home-hero-banner-slide__how-to-title">
      <span>{{ t('fair_home.hero.banner.how_to_line1') }}</span>
      <span>{{ t('fair_home.hero.banner.how_to_line2') }}</span>
      <span>{{ t('fair_home.hero.banner.how_to_line3') }}</span>
    </p>

    <div
      class="fair-home-hero-banner-slide__media fair-home-hero-banner-slide__media--front"
      aria-hidden="true"
    >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--question"
        :src="bannerQuestionUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
      <img
        class="fair-home-hero-banner-slide__deco fair-home-hero-banner-slide__deco--search"
        :src="bannerSearchUrl"
        alt=""
        loading="lazy"
        decoding="async"
        fetchpriority="low"
      >
    </div>
  </div>
</template>

<style scoped>
.fair-home-hero-banner-slide--how-to {
  /* Design frame 835×736 — Figma 1:8863 */
  --fair-banner-how-to-w: 835;
  --fair-banner-how-to-h: 736;
  --fair-banner-how-to-title-size: 170;
  --fair-banner-how-to-title-tracking: -6.8;
  --fair-banner-how-to-title-top-offset: 229;

  position: absolute;
  inset: 0;
}

/* Mobile sheet — Figma 1:12418 (361×553) */
.fair-home-hero-banner-slide--how-to.fair-home-hero-banner-slide--sheet {
  --fair-banner-how-to-w: 361;
  --fair-banner-how-to-h: 553;
  --fair-banner-how-to-title-size: 74.9;
  --fair-banner-how-to-title-tracking: -3;
  --fair-banner-how-to-title-top-offset: 100.56;
}

.fair-home-hero-banner-slide__media {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.fair-home-hero-banner-slide__media--back {
  z-index: 0;
}

.fair-home-hero-banner-slide__media--front {
  z-index: 2;
}

.fair-home-hero-banner-slide__bg {
  position: absolute;
  left: -123%;
  top: -18%;
  width: 230%;
  height: 125%;
  max-width: none;
  object-fit: cover;
  object-position: bottom;
}

.fair-home-hero-banner-slide__deco {
  position: absolute;
  object-fit: cover;
}

.fair-home-hero-banner-slide__deco--plus {
  left: 22.6%;
  top: 11%;
  width: 18.2%;
  aspect-ratio: 1;
  filter: blur(1.15px);
}

.fair-home-hero-banner-slide__deco--question {
  left: 50%;
  top: 19.16%;
  width: 35.79%;
  aspect-ratio: 1;
  translate: -27.6% 0;
  rotate: 23.41deg;
}

.fair-home-hero-banner-slide__deco--camera {
  right: 1.5%;
  top: 0.7%;
  width: 16.5%;
  aspect-ratio: 1;
  rotate: 48deg;
  filter: blur(1.15px);
}

.fair-home-hero-banner-slide__deco--arrow {
  left: 23.2%;
  bottom: 25.4%;
  width: 24.1%;
  aspect-ratio: 1;
  rotate: 18.81deg;
  filter: blur(0.75px);
}

.fair-home-hero-banner-slide__deco--search {
  right: 9.7%;
  bottom: 18.5%;
  width: 20.5%;
  aspect-ratio: 1;
}

.fair-home-hero-banner-slide__deco--image {
  left: 0;
  bottom: 6.7%;
  width: 20.8%;
  aspect-ratio: 1;
  opacity: 0.9;
  filter: blur(1.35px);
}

.fair-home-hero-banner-slide__deco--like {
  right: 15%;
  bottom: -9%;
  width: 23.3%;
  aspect-ratio: 1;
  rotate: -15deg;
  opacity: 0.8;
  filter: blur(1.3px);
}

.fair-home-hero-banner-slide__deco--cloth {
  left: -8.8%;
  top: 14%;
  width: 24.1%;
  aspect-ratio: 1;
  rotate: 57deg;
  opacity: 0.8;
  filter: blur(2.5px);
}

.fair-home-hero-banner-slide__how-to-title {
  position: absolute;
  left: 50%;
  top: calc(
    50%
      - (
        var(--fair-banner-how-to-title-top-offset)
          / var(--fair-banner-how-to-h) * 100%
      )
  );
  z-index: 1;
  display: flex;
  flex-direction: column;
  margin: 0;
  translate: -50% 0;
  font-family: 'Gilroy', sans-serif;
  font-weight: 700;
  font-style: normal;
  font-size: calc(
    var(--fair-banner-how-to-title-size) / var(--fair-banner-how-to-w) * 100cqi
  );
  line-height: 0.9;
  letter-spacing: calc(
    var(--fair-banner-how-to-title-tracking)
      / var(--fair-banner-how-to-title-size) * 1em
  );
  color: var(--fair-color-white-100, #fff);
  text-align: center;
  white-space: nowrap;
  pointer-events: none;
}

.fair-home-hero-banner-slide__how-to-title span {
  display: block;
  margin: 0;
  line-height: 0.9;
  font-weight: inherit;
}

/* Mobile deco positions — Figma 1:12418 on 361×553 */
.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--plus {
  left: 22.9%;
  top: 6.5%;
  width: 18.2%;
  filter: blur(0.5px);
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--cloth {
  left: 6%;
  top: 8.3%;
  width: 24.1%;
  filter: blur(1.1px);
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--camera {
  left: 87.3%;
  right: auto;
  top: 0.6%;
  width: 23.2%;
  translate: -50% 0;
  filter: blur(0.5px);
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--question {
  left: 55.4%;
  top: 24%;
  width: 35.8%;
  translate: 0 0;
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--arrow {
  left: 31.3%;
  top: 62.7%;
  bottom: auto;
  width: 24.1%;
  filter: blur(0.3px);
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--search {
  left: 70.1%;
  right: auto;
  top: 75.6%;
  bottom: auto;
  width: 20.5%;
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--image {
  left: 0.4%;
  top: 82.5%;
  bottom: auto;
  width: 20.8%;
  filter: blur(0.6px);
}

.fair-home-hero-banner-slide--sheet .fair-home-hero-banner-slide__deco--like {
  left: 61.2%;
  right: auto;
  top: 93.3%;
  bottom: auto;
  width: 23.3%;
  filter: blur(0.6px);
}
</style>
